export interface ConversationContextEnvironment {
  href: string;
  referrer: string;
  cookie: string;
  landingUrl?: string;
  rateFinderSessionId?: string;
  hopeMarkers?: { [key: string]: string };
  retainedAdAttribution?: any;
}

export const AD_ATTRIBUTION_STORAGE_KEY = 'fru_rate_finder_ad_attribution';

export function persistConversationAdAttribution(
  storage: Pick<Storage, 'setItem' | 'removeItem'>, attribution: any,
): void {
  if (attribution) {
    try {
      storage.setItem(AD_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
      return;
    } catch (e) { /* a failed replacement must not retain the previous ad identity */ }
  }
  try { storage.removeItem(AD_ATTRIBUTION_STORAGE_KEY); }
  catch (e) { /* unavailable storage must not prevent a conversation */ }
}

const AD_SOURCES: { [key: string]: string } = {
  google: 'google', googleads: 'google', adwords: 'google',
  bing: 'bing', bingads: 'bing', microsoftads: 'bing',
  meta: 'meta', facebook: 'meta', facebookads: 'meta', fb: 'meta',
  tiktok: 'tiktok', tiktokads: 'tiktok', tiktokvideos: 'tiktok',
  openai: 'openai', openaiads: 'openai', chatgptads: 'openai',
};

function adIdentifier(value: string, source: string, kind: string): boolean {
  if (typeof value !== 'string' || value.length > 64) { return false; }
  if (source === 'openai') {
    const prefix = kind === 'creative_id' ? 'ad_' : kind === 'campaign_id' ? 'cmpn_' : 'adgrp_';
    return value.indexOf(prefix) === 0 && /^[A-Za-z0-9_-]+$/.test(value) && value.length > prefix.length;
  }
  // Ad IDs are separate from free-text UTMs; short phone-like values stay excluded.
  return kind === 'creative_id' ? /^\d{12,20}$/.test(value) : /^\d{8,20}$/.test(value);
}

function validatedAdAttribution(candidate: any): any {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) { return undefined; }
  if (typeof candidate.traffic_source !== 'string'
      || AD_SOURCES[candidate.traffic_source] !== candidate.traffic_source) { return undefined; }
  const result: any = { traffic_source: candidate.traffic_source };
  if (candidate.traffic_source === 'meta' && typeof candidate.utm_content === 'string'
      && /^[A-Za-z][A-Za-z0-9_-]{0,190}$/.test(candidate.utm_content) && !containsPii(candidate.utm_content)) {
    result.utm_content = candidate.utm_content;
  }
  for (const key of ['creative_id', 'campaign_id', 'ad_group_id']) {
    if (candidate[key] === undefined && (key !== 'creative_id' || result.utm_content)) { continue; }
    if (!adIdentifier(candidate[key], candidate.traffic_source, key)) { return undefined; }
    result[key] = candidate[key];
  }
  return result;
}

function conversationAdAttribution(url: URL, retained: any): any {
  if (url.protocol !== 'https:' || url.username || url.password || (url.port && url.port !== '443')
      || ['www.freerateupdate.com', 'freerateupdate.com'].indexOf(url.hostname.toLowerCase()) < 0) {
    return undefined;
  }
  const query = url.searchParams;
  const sourceKeys = ['traffic_source', 'utm_source'];
  const idKeys = ['creative_id', 'creative', 'ad_id'];
  const attributionKeys = sourceKeys.concat(idKeys, [
    'utm_content', 'campaign_id', 'campaignid', 'utm_campaign', 'ad_group_id', 'adgroupid',
  ]);
  if (attributionKeys.some((key) => query.has(key + '[]'))) { return undefined; }
  const hasCurrentAttribution = attributionKeys.some((key) => query.has(key));
  if (!hasCurrentAttribution) { return validatedAdAttribution(retained); }
  const sources: string[] = [];
  for (const key of sourceKeys) {
    for (const value of query.getAll(key)) {
      const source = AD_SOURCES[value.toLowerCase()];
      if (!source) { return undefined; }
      if (sources.indexOf(source) < 0) { sources.push(source); }
    }
  }
  if (sources.length !== 1) { return undefined; }
  const source = sources[0];
  const result: any = { traffic_source: source };
  if (source === 'meta') {
    const aliases = query.getAll('utm_content').filter((value) => !adIdentifier(value, source, 'creative_id'));
    if (new Set(aliases).size > 1) { return undefined; }
    if (aliases.length) { result.utm_content = aliases[0]; }
  }
  const groups: { [key: string]: string[] } = {
    creative_id: idKeys.concat(['utm_content']),
    campaign_id: ['campaign_id', 'campaignid', 'utm_campaign'],
    ad_group_id: ['ad_group_id', 'adgroupid'],
  };
  for (const kind of Object.keys(groups)) {
    const values: string[] = [];
    for (const key of groups[kind]) {
      if (query.has(key + '[]')) { return undefined; }
      for (const value of query.getAll(key)) {
        if (!adIdentifier(value, source, kind)) {
          if (key === 'utm_content' || key === 'utm_campaign') { continue; }
          return undefined;
        }
        if (values.indexOf(value) < 0) { values.push(value); }
      }
    }
    if (values.length > 1) { return undefined; }
    if (values.length) { result[kind] = values[0]; }
  }
  return validatedAdAttribution(result);
}

const ATTRIBUTION_QUERY_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_keyword',
  'utm_content', 'utm_id', 'utm_source_platform', 'utm_creative_format',
  'utm_marketing_tactic', 'gclid', 'msclkid', 'fbclid', 'fclid', 'ttclid',
  'click_id', 'assistantSource', 'assistantEntry', 'experience', 'pk_ab_test',
  'pk_ab_test2', 'type', 'LoanPurpose', 'FormType', 'lp_button', 'calc',
];

const ROUTING_QUERY_KEYS = [
  'assistantSource', 'assistantEntry', 'type', 'LoanPurpose', 'FormType', 'lp_button', 'calc',
];

const UTM_QUERY_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_keyword',
  'utm_content', 'utm_id', 'utm_source_platform', 'utm_creative_format',
  'utm_marketing_tactic',
];

function containsPii(value: string): boolean {
  return /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(value)
    || /(?:^|\D)(?:\+?\d[\s().-]*){10,15}(?!\d)/.test(value);
}

function bounded(value: any, limit: number = 191): string {
  if (value === null || value === undefined || Array.isArray(value) || typeof value === 'object') { return ''; }
  const normalized = String(value).replace(/[\x00-\x1F\x7F]/g, '').trim().slice(0, limit);
  return containsPii(normalized) ? '' : normalized;
}

export function parseConversationCookies(rawCookie: string): { [key: string]: string } {
  const cookies: { [key: string]: string } = {};
  (rawCookie || '').split(';').forEach((part) => {
    const eq = part.indexOf('=');
    if (eq < 0) { return; }
    const key = part.slice(0, eq).trim();
    if (!key) { return; }
    try { cookies[key] = decodeURIComponent(part.slice(eq + 1).trim()); }
    catch (e) { /* malformed cookies carry no attribution */ }
  });
  return cookies;
}

function parsedCookieObject(cookies: { [key: string]: string }, name: string): any {
  try {
    let parsed = JSON.parse(cookies[name] || 'null');
    // PostLead serializes an already serialized lead-referrer value. Accept
    // exactly that one additional layer, then require a plain object.
    if (typeof parsed === 'string') { parsed = JSON.parse(parsed); }
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
}

export function sanitizeConversationUrl(raw: string, includeMarketing: boolean): string {
  if (!raw) { return ''; }
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') { return ''; }
    const allowed = includeMarketing ? ATTRIBUTION_QUERY_KEYS : ROUTING_QUERY_KEYS;
    const params = new URLSearchParams();
    allowed.forEach((key) => {
      const value = bounded(url.searchParams.get(key));
      if (value) { params.set(key, value); }
    });
    url.username = '';
    url.password = '';
    url.hash = '';
    try {
      if (containsPii(decodeURIComponent(url.pathname))) { url.pathname = '/'; }
    } catch (e) {
      url.pathname = '/';
    }
    url.search = params.toString();
    return url.toString().slice(0, 2048);
  } catch (e) {
    return '';
  }
}

function safeRequestUri(raw: string, origin: string, includeMarketing: boolean): string {
  if (!raw || raw.charAt(0) !== '/') { return ''; }
  const absolute = sanitizeConversationUrl(origin + raw, includeMarketing);
  if (!absolute) { return ''; }
  try {
    const url = new URL(absolute);
    return url.pathname + url.search;
  } catch (e) {
    return '';
  }
}

function classifySurface(url: URL, markers: { [key: string]: string }): string {
  const host = url.hostname.toLowerCase();
  const path = url.pathname.toLowerCase();
  const source = bounded(markers['hope_assistant_source'] || url.searchParams.get('assistantSource'), 64);
  const entry = bounded(markers['hope_assistant_entry'] || url.searchParams.get('assistantEntry'), 64);
  const pageType = bounded(url.searchParams.get('type') || url.searchParams.get('calc'), 64).toLowerCase();

  if (host === 'lowratefinder.ai' || host.endsWith('.lowratefinder.ai')
      || host === 'airatefinder.com' || host.endsWith('.airatefinder.com')) {
    return 'lowratefinder_standalone';
  }
  if (source === 'site_widget') { return 'embedded_widget'; }
  if (source === 'calculator' || pageType.indexOf('calculator') >= 0 || path.indexOf('calculator') >= 0) {
    return 'fru_calculator';
  }
  if (source === 'landing' || entry === 'v6_tile' || entry === 'ab_formlanding') { return 'fru_landing'; }
  if (host.indexOf('homeequity') >= 0 || path.indexOf('home-equity') >= 0
      || path.indexOf('homeequity') >= 0 || path.indexOf('/heloc') >= 0
      || pageType.indexOf('equity') >= 0 || pageType === 'heloc') {
    return 'home_equity_landing';
  }
  if (path.indexOf('/communication/rates-form') >= 0) { return 'fru_rates_form'; }
  return 'consumer_form_chat';
}

function copyAllowed(source: any, keys: string[]): any {
  const out: any = {};
  keys.forEach((key) => {
    const value = bounded(source && source[key], 512);
    if (value) { out[key] = value; }
  });
  return out;
}

/** Build the bounded context included in the existing chat/action POST. */
export function buildConversationContext(env: ConversationContextEnvironment): any {
  let current: URL;
  try { current = new URL(env.href); }
  catch (e) { return {}; }

  const cookies = parseConversationCookies(env.cookie);
  const optedOut = Object.prototype.hasOwnProperty.call(cookies, 'fruRemoveCookies');
  const markers = env.hopeMarkers || {};
  const leadReferrer = optedOut ? {} : parsedCookieObject(cookies, 'lead_referrer');
  const leadReferrerString = optedOut ? {} : parsedCookieObject(cookies, 'lead_referrer_string');
  const query = current.searchParams;

  const first = (queryName: string, cookieName?: string): string => {
    const fromQuery = bounded(query.get(queryName));
    if (fromQuery) { return fromQuery; }
    return bounded(leadReferrer[cookieName || queryName]);
  };

  const utmParams: any = {};
  if (!optedOut) {
    query.forEach((_rawValue, key) => {
      if (UTM_QUERY_KEYS.indexOf(key) < 0) { return; }
      const value = bounded(query.get(key));
      if (value) { utmParams[key] = value; }
    });
  }

  const assistantSource = bounded(markers['hope_assistant_source'] || query.get('assistantSource'), 64);
  const assistantEntry = bounded(markers['hope_assistant_entry'] || query.get('assistantEntry'), 64);
  const context: any = {
    ai_surface: classifySurface(current, markers),
    assistant_source: assistantSource,
    assistant_entry: assistantEntry,
    domain: current.hostname.toLowerCase().slice(0, 255),
    page_path: current.pathname.slice(0, 255),
    landing_url: sanitizeConversationUrl(env.landingUrl || env.referrer || env.href, !optedOut),
    current_url: sanitizeConversationUrl(env.href, !optedOut),
    referrer: optedOut ? '' : sanitizeConversationUrl(env.referrer, true),
  };

  if (!optedOut) {
    context.ad_attribution_checked = true;
    context.ad_attribution = conversationAdAttribution(current, env.retainedAdAttribution);
    const experiment = bounded(markers['experiment'] || query.get('experience'), 100);
    const variation = bounded(markers['variation'], 100);
    context.experiment_id = bounded(query.get('pk_ab_test2') || query.get('pk_ab_test') || experiment, 64);
    context.experiment_name = experiment;
    context.variation = variation;
    context.experiment_arm = variation;
    context.rate_finder_session_id = bounded(env.rateFinderSessionId || cookies['rate_finder_session_id'], 64);
    context.fru_vid = bounded(cookies['fru_vid'], 64);
    context.lead_referrer = copyAllowed(leadReferrer, [
      'source', 'medium', 'campaign', 'keyword', 'content', 'network', 'placement',
      'gclid', 'clickid', 'ttclid', 'tt_test_id', 'oppref',
    ]);
    context.lead_referrer_string = {};
    const leadHttpReferrer = sanitizeConversationUrl(bounded(leadReferrerString['http_referer'], 2048), true);
    const leadRequestUri = safeRequestUri(
      bounded(leadReferrerString['request_uri'], 2048),
      current.origin,
      true,
    );
    if (leadHttpReferrer) { context.lead_referrer_string.http_referer = leadHttpReferrer; }
    if (leadRequestUri) { context.lead_referrer_string.request_uri = leadRequestUri; }
    context.utm_source = first('utm_source', 'source');
    context.utm_medium = first('utm_medium', 'medium');
    context.utm_campaign = first('utm_campaign', 'campaign');
    context.utm_term = first('utm_term', 'keyword') || bounded(query.get('utm_keyword'));
    context.utm_content = first('utm_content', 'content');
    context.utm_params = utmParams;
    context.gclid = first('gclid', 'gclid');
    context.msclkid = first('msclkid', 'clickid');
    context.fbclid = first('fbclid');
    context.fclid = first('fclid');
    context.ttclid = first('ttclid', 'ttclid');
    context.ga_client_id = bounded(cookies['_ga'], 128);
    const matomoKey = Object.keys(cookies).find((key) => key.indexOf('_pk_id.') === 0);
    context.matomo_visitor_id = matomoKey ? bounded((cookies[matomoKey] || '').split('.')[0], 64) : '';
  }

  Object.keys(context).forEach((key) => {
    if (context[key] === '' || context[key] === null || context[key] === undefined
        || (typeof context[key] === 'object' && Object.keys(context[key]).length === 0)) {
      delete context[key];
    }
  });

  return context;
}
